import SearchBar from "@/components/SearchBar";
import CategoryCarousel from "@/components/CategoryCarousel";
import ListingCard from "@/components/ListingCard";
import ServicesCarousel from "@/components/ServicesCarousel";
import RecentlyViewed from "@/components/RecentlyViewed";
import AdoptionCarousel from "@/components/AdoptionCarousel";
import RecommendedForYou from "@/components/RecommendedForYou";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";

import { browseApi } from "@/api/browse";
import { logger } from "@/utils/logger";

// Local component copied from App.js (behavior preserved)
const ListingsCarousel = ({ species, title }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        setError(null);
        const data = await browseApi.browse(species, { limit: 6 });
        setListings(data.listings || []);
      } catch (err) {
        logger.error(`Failed to fetch ${species} listings:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [species]);

  if (loading) {
    return (
      <div className="mb-16">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl text-[#EDEDED]">{title}</h3>
        </div>
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
        </div>
      </div>
    );
  }
  
  if (error || listings.length === 0) return null;

  return (
    <div className="mb-16">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl text-[#EDEDED]">{title}</h3>
        <Link to={`/browse/${species}`} className="text-sm text-[#D4AF37] flex items-center gap-1 hover:underline">
          View All <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {listings.slice(0, 3).map((listing) => (
          <ListingCard key={listing.listing_id} listing={listing} />
        ))}
      </div>
    </div>
  );
};

// These sections are still defined in App.js; imported later in refactor step 2
// To preserve behavior with minimal risk, we re-export placeholders that will be replaced.

const Home = ({ TrustPillarsSection, BreederCTASection, Footer, user, login }) => {
  return (
    <div className="pt-16">
      <section className="hero-section noise-overlay min-h-[80vh]" data-testid="hero-section">
        <div className="hero-bg">
          <img
            src="https://images.pexels.com/photos/39070/white-horse-white-horse-black-background-39070.jpeg"
            alt="Majestic white horse"
            loading="eager"
            decoding="sync"
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
          />
          <div className="hero-gradient absolute top-0 left-0 w-full h-full" />
        </div>
        <div className="hero-content">
          <h1
            className="font-medium text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[1.1] text-[#EDEDED] mb-6 opacity-0 animate-fade-in-up"
            style={{ animationDelay: "200ms", animationFillMode: "forwards" }}
          >
            Breedling
          </h1>
          <p
            className="text-xl md:text-2xl text-[#D4AF37] font-light mb-4 opacity-0 animate-fade-in-up"
            style={{ animationDelay: "400ms", animationFillMode: "forwards" }}
          >
            The premium marketplace for verified breeders.
          </p>
          <p
            className="text-base md:text-lg text-[#A1A1AA] font-light max-w-2xl mx-auto mb-12 leading-relaxed opacity-0 animate-fade-in-up"
            style={{ animationDelay: "600ms", animationFillMode: "forwards" }}
          >
            Welcome to Breedling&trade; &mdash; discover elite bloodlines. Connect with verified breeders nationwide.
          </p>
          <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: "800ms", animationFillMode: "forwards" }}>
            <SearchBar />
          </div>
          
          {/* Primary CTAs */}
          <div 
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up"
            style={{ animationDelay: "1000ms", animationFillMode: "forwards" }}
          >
            <Link 
              to="/litters" 
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#C4A030] transition-colors"
              data-testid="cta-offspring"
            >
              Find Available Litters
            </Link>
            <Link 
              to="/studs" 
              className="px-6 py-3 bg-[#18181B] text-[#EDEDED] font-medium rounded-lg border border-[#3F3F46] hover:border-[#D4AF37] transition-colors"
              data-testid="cta-studs"
            >
              Browse Stud Services
            </Link>
            <Link 
              to="/mares" 
              className="px-6 py-3 bg-[#18181B] text-[#EDEDED] font-medium rounded-lg border border-[#3F3F46] hover:border-[#D4AF37] transition-colors"
              data-testid="cta-mares"
            >
              Browse Mare Services
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Browse by Category</p>
          <h2 className="text-4xl md:text-5xl tracking-tight text-[#EDEDED] mb-12">Find your next Breedling</h2>
          <CategoryCarousel />
        </div>
      </section>
      
      {/* Farm-Grade Breeding Stock Section */}
      <section className="py-24 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-12">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Farm-Grade Breeding Stock</p>
              <h2 className="text-4xl md:text-5xl tracking-tight text-[#EDEDED]">Expand your gene pool</h2>
            </div>
            <Link 
              to="/farm-breeding"
              className="mt-6 md:mt-0 px-6 py-3 bg-[#22C55E] text-black font-medium rounded-lg hover:bg-[#16A34A] transition-colors flex items-center gap-2"
              data-testid="cta-farm-breeding"
            >
              Browse Farm Breeding Stock <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass-card p-6">
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/10 flex items-center justify-center mb-4">
                <span className="text-2xl">🐴</span>
              </div>
              <h3 className="text-xl text-[#EDEDED] mb-2">Stud Services</h3>
              <p className="text-[#A1A1AA] text-sm">Access verified sires for live cover, AI, or shipped semen breeding programs.</p>
            </div>
            <div className="glass-card p-6">
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/10 flex items-center justify-center mb-4">
                <span className="text-2xl">🌾</span>
              </div>
              <h3 className="text-xl text-[#EDEDED] mb-2">Mare Services</h3>
              <p className="text-[#A1A1AA] text-sm">Find quality mares for breeding partnerships and program expansion.</p>
            </div>
            <div className="glass-card p-6">
              <div className="w-12 h-12 rounded-full bg-[#22C55E]/10 flex items-center justify-center mb-4">
                <span className="text-2xl">✓</span>
              </div>
              <h3 className="text-xl text-[#EDEDED] mb-2">Verified Health Docs</h3>
              <p className="text-[#A1A1AA] text-sm">All breeding stock listings require uploaded health certifications.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Featured Listings</p>
          <h2 className="text-4xl md:text-5xl tracking-tight text-[#EDEDED] mb-12">Recently Added</h2>
          <ListingsCarousel species="equine" title="Equine" />
          <ListingsCarousel species="canine" title="Canine" />
          <ListingsCarousel species="feline" title="Feline" />
          <div className="mt-16">
            <ServicesCarousel />
          </div>
        </div>
      </section>

      {/* Adoption Carousel */}
      <AdoptionCarousel />

      {/* Recommended for You */}
      <RecommendedForYou user={user} />

      {/* Recently Viewed Section */}
      <RecentlyViewed />

      {TrustPillarsSection ? <TrustPillarsSection /> : null}
      {BreederCTASection ? <BreederCTASection user={user} login={login} /> : null}
      {Footer ? <Footer /> : null}
    </div>

  );
};

export default Home;
